import { ref } from 'vue';

/**
 * Composable for handling async operations
 * @param {Function} fn - The async function to execute
 * @param {Object} options - Configuration options
 * @param {boolean} [options.immediate=false] - Whether to execute the function immediately
 * @param {Function} [options.onSuccess] - Callback function called when the async operation succeeds
 * @param {Function} [options.onError] - Callback function called when the async operation fails
 * @param {Function} [options.onFinally] - Callback function called when the async operation completes (success or failure)
 */
export function useAsyncFn(fn, options = { immediate: false }) {
  const isExecuted = ref(false);
  const isLoading = ref(false);
  const isError = ref(false);

  const execute = async (...params) => {
    try {
      isLoading.value = true;

      const result = await fn(...params);

      if (!isExecuted.value) isExecuted.value = true;

      if (options.onSuccess) options.onSuccess(result);
    } catch (error) {
      isError.value = true;

      if (options.onError) options.onError(error);
      throw error;
    } finally {
      isLoading.value = false;

      if (options.onFinally) options.onFinally();
    }
  };

  // Execute the function immediately if the immediate option is true
  if (options.immediate) {
    execute();
  }

  return { isLoading, isError, isExecuted, execute };
}
